import React, { memo, useMemo, useEffect, useState } from "react";
import { FlatList } from "react-native";
import OrderCartItemComponent from "@components/molecules/OrderCartItemCard/index";
import { useTheme } from "styled-components/native";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { OrderCartSummaryListProps } from "./types";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import EditBulkItemModal from "@components/organisms/BulkInitSummaryList/EditBulkItemModal";
import { useSelectedItems } from "../../../contexts/SelectedItemsContext";
import { BreadCrumbLabel } from "@components/molecules/Breadcrumbs/utils";
import { ProductCategory, SectionName } from "@utils/constants";
import { setBuilderProduct, setProductType } from "@redux/slices/appStateSlice";
import { setSectionName } from "@redux/slices/searchFilters";
import { useDispatch } from "react-redux";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { getSectionName } from "@utils/product/searchResultsNavigation";
import { showRequestItemFromSupplier } from "@scenes/requests/utils/utils";

const OrderCartSummaryList = ({
  itemList,
  priceMode,
  showLoader,
  setShowLoader,
  refetchCartData,
  productType,
  showRemoveIcon,
  orderCartRequest,
  isChildRequest = false,
  fromModifyScreen = false,
  requestType,
  saveBulkItemDetails,
  showCheckbox,
  checkBoxSelection,
  _removeItemFromList,
  RemoveFromBulkListConfirmPopup,
  openRemoveFromBulkPopupId,
  stockItemRequest,
}: OrderCartSummaryListProps) => {
  const theme = useTheme();
  const productPrefix = useProductPrefix(productType);
  const { selectedItems } = useSelectedItems();
  const [editCommentModalItemIndex, setEditCommentModalItemIndex] = useState<
    number | null
  >(null);
  const dispatch = useDispatch();
  const { addRouteToHistory, clearRouteHistory } = useNavigationHistory();
  const { enableJewelryBuilder } = useOrgSettingsSlice();

  useEffect(() => {
    if (showLoader) {
      setShowLoader(false);
    }
  }, [showLoader]);

  const changeEditCommentModalIndex = (id: number) => {
    let index =
      itemList.findIndex((item: any) =>
        fromModifyScreen ? item?.id === id : item.stock_item_id === id
      ) ?? null;
    if (index < 0) index = null;
    setEditCommentModalItemIndex(index);
  };

  const _saveBulkItemDetails = (
    id: number,
    details: any,
    fromParcel?: boolean
  ) => {
    if (id && details) {
      saveBulkItemDetails(id, details);
      if (!fromParcel) {
        setEditCommentModalItemIndex(null);
      }
    } else {
      setEditCommentModalItemIndex(null);
    }
  };

  const RenderedItem = memo(({ item, i }: any) => {
    const isBuilderItem = !!item.group_reference_id;
    return (
      <OrderCartItemComponent
        key={i}
        mItem={item}
        priceMode={priceMode}
        changeEditCommentModalIndex={
          fromModifyScreen ? changeEditCommentModalIndex : _removeItemFromList
        }
        setShowLoader={setShowLoader}
        showRequestItemFromSupplier={showRequestItemFromSupplier(item)}
        deepLink={stockItemRequest?.deep_link ?? ""}
        refetchCartData={refetchCartData}
        productType={productType}
        showRemoveIcon={showRemoveIcon}
        orderCartRequest={orderCartRequest}
        isChildRequest={isChildRequest}
        fromRequestModify={fromModifyScreen}
        showCheckbox={showCheckbox}
        checkBoxSelection={checkBoxSelection}
        selectedItems={selectedItems}
        applyMarginTop={i === 0}
        applyMarginBottom={i !== itemList.length - 1}
        onPress={() => {
          const additionalAttributes = item?.additional_attributes || {};

          const transformed = Object.keys(additionalAttributes).reduce(
            (result, key) => {
              const value = additionalAttributes[key];
              if (value === null || value === "") {
                return result;
              }
              if (key === "personalization") {
                result.personalization = value;
              } else if (key === "config" && typeof value === "object") {
                result.config = Object.keys(value).reduce((cfg, cfgKey) => {
                  const cfgValue = value[cfgKey];
                  if (cfgValue === null || cfgValue === "") {
                    return cfg;
                  }
                  if (Array.isArray(cfgValue)) {
                    cfg[cfgKey] = cfgValue;
                  } else if (typeof cfgValue === "object") {
                    cfg[cfgKey] = { ...cfgValue };
                  } else {
                    cfg[cfgKey] = cfgValue;
                  }
                  return cfg;
                }, {});
              }
              return result;
            },
            {}
          );
          const pdpParams = {
            breadCrumbLabel:
              item.stock_type === ProductCategory.JEWELRY ||
              item.stock_type === ProductCategory.LABGROWN_JEWELRY
                ? BreadCrumbLabel.SETTING_DETAILS
                : BreadCrumbLabel.STONE_DETAILS,
            productID: item.stock_item_id ?? item?.id,
            productType: item.stock_type ?? item?.type,
            ...(Object.keys(transformed || {}).length > 0
              ? { additionalAttributes: JSON.stringify(transformed) }
              : {}),
            productPrefix,
          };
          const reviewParams = {
            breadCrumbLabel: BreadCrumbLabel.REVIEW,
            enterSecondFlow: true,
            priceMode: priceMode,
            productPrefix: ProductCategory.JEWELRY,
            productType: ProductCategory.JEWELRY,
            additionalAttributes: JSON.stringify(transformed),
            itemDetail: {
              ...item,
              id: item?.actual_id,
              ...(orderCartRequest && { type: item.stock_type }),
            },
            stoneItemDetail: {
              ...item?.stoneItemDetails,
              id: item?.stoneItemDetails?.actual_id,
              ...(orderCartRequest && {
                type: item?.stoneItemDetails?.stock_type,
              }),
            },
            matchingPairItemDetail: {
              ...item?.matchingPairItemDetails,
              id: item?.matchingPairItemDetails?.actual_id,
              ...(orderCartRequest && {
                type: item?.matchingPairItemDetails?.stock_type,
              }),
            },
            fromOrderCart: true,
          };
          const mRoute = { name: Routes.ReviewPage, params: reviewParams };
          const requestRoute = {
            name: Routes.OrderCartScreen,
            params: {
              breadCrumbLabel: BreadCrumbLabel.ORDER_CART,
            },
          };
          if (enableJewelryBuilder) {
            if (isBuilderItem) {
              dispatch(setProductType(ProductCategory.JEWELRY));
              dispatch(setSectionName(SectionName.JEWELRY));
              dispatch(setBuilderProduct(ProductCategory.JEWELRY));
              clearRouteHistory();
              addRouteToHistory(requestRoute);
              addRouteToHistory(mRoute);
            } else {
              const pdpRoute = { name: Routes.PDPScreen, params: pdpParams };

              dispatch(setProductType(pdpParams.productType));
              dispatch(
                setSectionName(getSectionName(pdpParams.productType, ""))
              );
              dispatch(setBuilderProduct(pdpParams.productType));
              clearRouteHistory();
              addRouteToHistory(pdpRoute);
            }
          }

          const screenName = isBuilderItem
            ? Routes.ReviewPage
            : Routes.PDPScreen;
          navigate(Routes.AppNavigator, {
            screen: Routes.ExploreNavigator,
            params: {
              screen: screenName,
              params: {
                ...(isBuilderItem ? reviewParams : pdpParams),
              },
            },
          });
        }}
        isBuilderItem={isBuilderItem}
        {...(item?.stoneItemDetails && { stoneItem1: item?.stoneItemDetails })}
        {...(item?.matchingPairItemDetails && {
          stoneItem2: item?.matchingPairItemDetails,
        })}
      />
    );
  });

  const EditBulkModal = () => {
    return (
      <EditBulkItemModal
        item={
          editCommentModalItemIndex !== null
            ? itemList[editCommentModalItemIndex]
            : 0
        }
        isOpen={editCommentModalItemIndex !== null}
        productType={
          fromModifyScreen
            ? itemList[editCommentModalItemIndex ?? 0]?.type
            : itemList[editCommentModalItemIndex ?? 0]?.stock_type ?? "diamond"
        }
        onClose={changeEditCommentModalIndex}
        removeFromList={_removeItemFromList}
        saveBulkItemDetails={_saveBulkItemDetails}
        requestType={requestType}
        fromRequestModify={fromModifyScreen}
        RemoveFromBulkListConfirmPopup={RemoveFromBulkListConfirmPopup}
        openRemoveFromBulkPopupId={openRemoveFromBulkPopupId}
      />
    );
  };

  const renderContent = useMemo(
    () => (
      <>
        <FlatList
          data={itemList}
          renderItem={({ item, index }) => {
            return <RenderedItem item={item} i={index} />;
          }}
          keyExtractor={(item) => item?.id?.toString()}
        />
      </>
    ),
    [itemList, theme]
  );

  return (
    <>
      {renderContent}
      {EditBulkModal()}
    </>
  );
};

export default memo(OrderCartSummaryList, (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.itemList) === JSON.stringify(nextProps.itemList) &&
    prevProps.showCheckbox === nextProps.showCheckbox &&
    prevProps.checkBoxSelection === nextProps.checkBoxSelection
  );
});
